import React, { useEffect, useState } from "react";
import AddServiceCategoryModal from "./AddServiceCategoryModal";
import {
  deleteServiceCategory,
  getServiceCategoryById,
  getServiceCategories,
} from "../../apis/serviceCategory";

const ServiceCategoriesList: React.FC = () => {
  const [categories, setCategories] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editableId, setEditableId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res: any = await getServiceCategories({ per_page: 100 });
      setCategories(res.data || res);
    } catch (error) {
      console.error("Error fetching service categories:", error);
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const res: any = await getServiceCategoryById(id);
      const d = res.data || res;
      setEditableId(id);
      setSelectedCategory({
        code: d.code,
        displayName: d.displayName || d.display_name,
        source: d.source,
        isClaimable: !!(d.isClaimable ?? d.is_claimable),
        isPharmacy: !!(d.isPharmacy ?? d.is_pharmacy),
        description: d.description || "",
        displayOrder: d.displayOrder ?? d.display_order ?? 0,
        isActive: !!(d.isActive ?? d.is_active),
      });
      setOpenModal(true);
    } catch (error) {
      console.error("Error fetching service category:", error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this service category?")) return;
    try {
      await deleteServiceCategory(id);
      fetchCategories();
    } catch (error) {
      console.error("Error deleting service category:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Insurance Service Categories
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Canonical category codes used for mapping insurance claim templates.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpenModal(true)}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Add Category
        </button>
      </div>

      <AddServiceCategoryModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditableId(null);
          setSelectedCategory(null);
        }}
        onSuccess={fetchCategories}
        editableId={editableId}
        categoryData={selectedCategory}
      />

      <div className="flow-root mt-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Code</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Display Name</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Source</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Claimable</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Pharmacy</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Active</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Order</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-4 text-center text-sm text-gray-500">
                        No service categories found
                      </td>
                    </tr>
                  ) : (
                    categories.map((cat: any) => (
                      <tr key={cat.id}>
                        <td className="py-4 px-3 text-sm font-mono font-medium text-gray-900">
                          {cat.code}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {cat.displayName || cat.display_name}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">{cat.source}</td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {(cat.isClaimable ?? cat.is_claimable) ? "Yes" : "No"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {(cat.isPharmacy ?? cat.is_pharmacy) ? "Yes" : "No"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {(cat.isActive ?? cat.is_active) ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {cat.displayOrder ?? cat.display_order}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(cat.id)}
                              className="px-3 py-1 text-sm text-white bg-primary rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(cat.id)}
                              className="px-3 py-1 text-sm text-white bg-red-600 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCategoriesList;
